
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { billingService } from '@/services/billing/billingService';
import { useToast } from '@/hooks/use-toast';

interface WaveSyncStatusProps {
  syncStatus?: 'pending' | 'synced' | 'failed';
  syncError?: string;
  waveId?: string;
  itemType: 'estimate' | 'invoice';
  itemId: string;
  onSyncComplete?: () => void;
}

const WaveSyncStatus: React.FC<WaveSyncStatusProps> = ({
  syncStatus = 'pending',
  syncError,
  waveId,
  itemType,
  itemId,
  onSyncComplete
}) => {
  const { toast } = useToast();
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleRetrySync = async () => {
    setIsRetrying(true);
    try {
      if (itemType === 'estimate') {
        await billingService.syncEstimateToWave(itemId);
      } else {
        await billingService.syncInvoiceToWave(itemId);
      }
      
      toast({
        title: 'Sync Successful',
        description: `${itemType} has been synced to Wave`,
      });
      
      onSyncComplete?.();
    } catch (error) {
      console.error('Sync retry failed:', error);
      toast({
        title: 'Sync Failed',
        description: `Failed to sync ${itemType} to Wave`,
        variant: 'destructive'
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const getSyncStatusBadge = () => {
    switch (syncStatus) {
      case 'synced':
        return (
          <Badge className="bg-green-500 text-white">
            <CheckCircle className="h-3 w-3 mr-1" />
            Synced to Wave
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500 text-white">
            <AlertCircle className="h-3 w-3 mr-1" />
            Sync Failed
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge className="bg-yellow-500 text-white">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Pending Sync
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-2">
      {getSyncStatusBadge()}
      
      {waveId && (
        <div className="text-xs text-gray-500">
          Wave ID: {waveId}
        </div>
      )}
      
      {syncError && (
        <div className="text-xs text-red-500">
          Error: {syncError}
        </div>
      )}
      
      {(syncStatus === 'failed' || syncStatus === 'pending') && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleRetrySync}
          disabled={isRetrying}
          className="text-xs"
        >
          {isRetrying ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          Retry Sync
        </Button>
      )}
    </div>
  );
};

export default WaveSyncStatus;
